;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React, { Fragment, memo } from 'react';

import PropTypes from 'prop-types';

// This component requires Contentful's Image API
// Inspiration: https://www.contentful.com/blog/2019/10/31/webp-source-sets-images-api/

// Contentful's max width is 4000px
const MAX_CONTENTFUL_WIDTH = 4000;
// 2x ratio
const RATIO_2X = 2;

const breakpoints = {
    medium: 768,
    large: 1024,
    xLarge: 1280,
    xxLarge: 1440,
    xxxLarge: 1920,
};

function getDefaultCustomSources(ogSrc) {
    const breakpointSources = Object.values(breakpoints).map(breakpoint => {
        return {
            breakpoint: breakpoint,
            src: ogSrc,
            imageWidth: breakpoint * 1.5,
        };
    });

    return [...breakpointSources, { src: ogSrc, imageWidth: 768 }];
}

const ContentfulImg = ({
    alt = '',
    className = null,
    customSources = [],
    decoding,
    draggable = false,
    fallbackImageWidth = 1280,
    loading = 'lazy',
    priority = false,
    src: ogSrc,
    ...rest
}) => {
    const hasNoFallbackCustomSource =
        customSources?.length > 0 &&
        customSources.every(source => source.breakpoint);

    hasNoFallbackCustomSource &&
        console.warn(
            'ContentfulImg.js: For optimization purposes, it is *highly recommended* that you include a fallback custom source with no breakpoint.'
        );

    const isSVG = /.svg$/i.test(ogSrc);
    const isGif = /.gif$/i.test(ogSrc);

    // Round with to nearest integer to keep Contentful's Image API happy
    const fallbackWidth = Math.round(fallbackImageWidth);
    const fallbackQuality = isGif ? 100 : 90;
    const quality = isGif ? 100 : 80;
    const sourcesCustom =
        customSources?.length > 0
            ? customSources
            : getDefaultCustomSources(ogSrc);

    const sources = customSources?.length > 0 ? sourcesCustom : [];
    const source = isSVG
        ? ogSrc
        : `${ogSrc}?w=${fallbackWidth}&q=${fallbackQuality}&fm=webp`;

    return (
        <picture>
            {sources.map((item, i) => {
                const {
                    breakpoint,
                    orientation,
                    imageWidth,
                    src: breakpointSrc,
                    width,
                    height,
                } = item;

                // Get the appropriate source URL for this breakpoint
                const sourceUrl = breakpointSrc ?? ogSrc;

                // For SVGs, don't apply image transformations
                const isSrcSVG = /.svg$/i.test(sourceUrl);

                // Max image values at 2560px and round width to nearest integer to keep Contentful's Image API happy
                const optimizedWidth = Math.round(Math.min(imageWidth, 2560));

                // Determine media query based on orientation or breakpoint
                let mediaQuery = null;
                if (orientation) {
                    mediaQuery = `(orientation: ${orientation})`;
                } else if (breakpoint) {
                    mediaQuery = `(min-width: ${breakpoint}px)`;
                }

                // Create appropriate source URLs based on image type
                const webpSourceUrl = isSrcSVG
                    ? sourceUrl
                    : `${sourceUrl}?w=${optimizedWidth}&fm=webp&q=${quality} 1x, ${sourceUrl}?w=${Math.min(
                          optimizedWidth * RATIO_2X,
                          MAX_CONTENTFUL_WIDTH
                      )}&fm=webp&q=${quality} 2x`;
                const fallbackSourceUrl = `${sourceUrl}?w=${optimizedWidth}&q=${quality}`;

                // Determine the MIME type for the source
                const mimeType = isSrcSVG ? 'image/svg+xml' : 'image/webp';

                // Per-source dimensions let the browser reserve the right
                // aspect ratio when sources differ in shape (avoids CLS)
                return (
                    <Fragment key={i}>
                        <source
                            media={mediaQuery}
                            srcSet={webpSourceUrl}
                            type={mimeType}
                            width={width}
                            height={height}
                        />
                        {!isSrcSVG && (
                            <source
                                media={mediaQuery}
                                srcSet={fallbackSourceUrl}
                                width={width}
                                height={height}
                            />
                        )}
                    </Fragment>
                );
            })}
            <img
                alt={alt}
                src={source}
                className={className}
                draggable={draggable}
                loading={priority ? 'eager' : loading}
                decoding={priority ? 'sync' : decoding}
                fetchPriority={priority ? 'high' : 'low'}
                {...rest}
            />
        </picture>
    );
};

ContentfulImg.propTypes = {
    alt: PropTypes.string,
    className: PropTypes.string,
    customSources: PropTypes.array,
    decoding: PropTypes.oneOf(['async', 'sync', 'auto']),
    draggable: PropTypes.bool,
    fallbackImageWidth: PropTypes.number,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    priority: PropTypes.bool,
    src: PropTypes.string.isRequired,
};

export default memo(ContentfulImg);
